import React, { useEffect, useState } from "react";

import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, Button, CircularProgress, Link, Typography } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";

import { listSCCareRecords } from "api/endpoints/sc-care";
import { callOptionsHeaders } from "api/helpers";
import { DEFAULT_REGION_CODE } from "constants/index";

interface SCCareRecordRow {
  id: string;
  orgId: string;
  policyNumber: string;
}

function CustomToolbar({
  onRefresh,
  isLoading,
}: {
  onRefresh: () => void;
  isLoading: boolean;
}) {
  return (
    <GridToolbarContainer sx={{ justifyContent: "space-between", padding: 1 }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <Button
          size="small"
          startIcon={
            isLoading ? <CircularProgress size={16} /> : <RefreshIcon />
          }
          onClick={onRefresh}
          disabled={isLoading}
          data-testid="sc-care-refresh-btn"
        >
          {isLoading ? "Loading..." : "Refresh"}
        </Button>
        <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
      </Box>
      <GridToolbarQuickFilter
        quickFilterParser={(searchInput: string) =>
          searchInput.split(" ").filter((word) => word !== "")
        }
        debounceMs={300}
        sx={{ width: 250 }}
      />
    </GridToolbarContainer>
  );
}

function CustomNoRowsOverlay() {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      height="100%"
    >
      <Typography variant="body2" color="textSecondary">
        No SC Care records found.
      </Typography>
    </Box>
  );
}

const columns: GridColDef[] = [
  {
    field: "policyNumber",
    headerName: "Policy Number",
    flex: 1,
    minWidth: 180,
  },
  {
    field: "orgId",
    headerName: "Organisation",
    flex: 1,
    minWidth: 300,
    renderCell: ({ row }) => (
      <Link href={`/organisation/${DEFAULT_REGION_CODE}/${row.orgId}`}>
        {row.orgId}
      </Link>
    ),
  },
];

const SCCareRecordsTable: React.FC = () => {
  const [records, setRecords] = useState<SCCareRecordRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRecords = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const headers = callOptionsHeaders(DEFAULT_REGION_CODE);
      const resp = await listSCCareRecords(headers);
      const rows: SCCareRecordRow[] = resp.records.map((r, index) => ({
        id: `${r.orgId}-${r.policyNumber}-${index}`,
        orgId: r.orgId,
        policyNumber: r.policyNumber,
      }));
      setRecords(rows);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load records";
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ width: "100%", pt: 2 }}>
      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ height: 500, width: "100%" }}>
        <DataGrid
          rows={records}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          disableRowSelectionOnClick
          slots={{
            toolbar: () => (
              <CustomToolbar onRefresh={loadRecords} isLoading={isLoading} />
            ),
            noRowsOverlay: CustomNoRowsOverlay,
          }}
          data-testid="sc-care-records-grid"
        />
      </Box>
    </Box>
  );
};

export default SCCareRecordsTable;
